// Categories.js - Same UI as Brands/Variations (City removed + Sidebar icon)

import React, { useState, useEffect, useRef } from 'react';
import { MdEditSquare } from "react-icons/md";
import { FaStore, FaEllipsisV, FaTrash, FaPrint, FaCheckDouble, FaSearch, FaPlus, FaTags, FaChevronRight } from 'react-icons/fa';
import { TbCategory } from 'react-icons/tb';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import './Categories.css';

// Last loaded categories of each shop are kept, so the list shows instantly
// on the next visit while fresh data loads in the background.
const CATEGORIES_CACHE_PREFIX = 'categories_cache_';

const readCategoriesCache = (id) => {
  if (!id || id === 'all') return null;
  try {
    const raw = localStorage.getItem(`${CATEGORIES_CACHE_PREFIX}${id}`);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
};

const writeCategoriesCache = (id, data) => {
  if (!id || id === 'all') return;
  try {
    localStorage.setItem(`${CATEGORIES_CACHE_PREFIX}${id}`, JSON.stringify(data));
  } catch (e) {}
};

const clearCategoriesCache = (id) => {
  if (!id) return;
  try {
    localStorage.removeItem(`${CATEGORIES_CACHE_PREFIX}${id}`);
  } catch (e) {}
};

const escapeHtml = (text) =>
  String(text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const Categories = () => {
  const { shopId: urlShopId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [shopId, setShopId] = useState(() => {
    const storedShopId = localStorage.getItem('selectedShopId');
    return urlShopId || storedShopId || null;
  });

  const [cards, setCards] = useState(() => {
    const startShopId = urlShopId || localStorage.getItem('selectedShopId');
    return readCategoriesCache(startShopId) || [];
  });
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(false);
  // True only while there is nothing to show yet (so "No Categories Found" never flashes)
  const [initialLoading, setInitialLoading] = useState(() => {
    const startShopId = urlShopId || localStorage.getItem('selectedShopId');
    return !readCategoriesCache(startShopId);
  });
  const [error, setError] = useState(null);

  const [selectAll, setSelectAll] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [shopName, setShopName] = useState('');
  const [shopsLoaded, setShopsLoaded] = useState(false);

  const [showActionDropdown, setShowActionDropdown] = useState(false);

  // Move To (copy to other shops)
  const [showMoveList, setShowMoveList] = useState(false);
  const [moveShopIds, setMoveShopIds] = useState([]);
  const [moveLoading, setMoveLoading] = useState(false);

  // Search term
  const [searchTerm, setSearchTerm] = useState('');

  const [newCard, setNewCard] = useState({
    image: null,
    imagePreview: null,
    category: '',
    selectedShopId: ''
  });

  const [editFormData, setEditFormData] = useState({
    id: null,
    image: null,
    imagePreview: null,
    category: '',
    shopId: null,
    shopName: ''
  });

  const categoriesRequestRef = useRef(0);

  const API_BASE_URL = 'https://super-backend.hira-tech.co.uk/api/product';

  const isAllShopsMode = shopId === 'all';
  const isNoShopId = !shopId;

  useEffect(() => {
    if (urlShopId) {
      setShopId(urlShopId);
      localStorage.setItem('selectedShopId', urlShopId);
    } else {
      const storedShopId = localStorage.getItem('selectedShopId');
      if (storedShopId) {
        setShopId(storedShopId);
      }
    }
  }, [urlShopId]);

  useEffect(() => {
    const handleShopChanged = (event) => {
      const { shopId: newShopId } = event.detail;
      setShopId(newShopId.toString());

      const currentPath = location.pathname;
      const urlMatch = currentPath.match(/^\/shop\/\d+(.*)$/);
      if (urlMatch) {
        const restOfPath = urlMatch[1] || '';
        navigate(`/shop/${newShopId}${restOfPath}`);
      } else {
        navigate(`/shop/${newShopId}/categories`);
      }
    };

    window.addEventListener('shopChanged', handleShopChanged);
    return () => window.removeEventListener('shopChanged', handleShopChanged);
  }, [location.pathname, navigate]);

  useEffect(() => {
    fetchShops();
  }, []);

  useEffect(() => {
    if (shopId) {
      // Show the saved list of this shop immediately (if there is one)
      const cached = (!isAllShopsMode) ? readCategoriesCache(shopId) : null;
      if (cached) {
        setCards(cached);
        setInitialLoading(false);
      } else {
        setCards([]);
        setInitialLoading(true);
      }
      fetchCategories('silent');
    } else {
      setInitialLoading(false);
    }
  }, [shopId]);

  useEffect(() => {
    if (shopId && !isAllShopsMode && !isNoShopId) {
      const shop = shops.find(s => s.shop_id === parseInt(shopId));
      if (shop) {
        setShopName(shop.shop_name);
      } else {
        setShopName(`Shop ${shopId}`);
      }
    } else {
      setShopName('');
    }
  }, [shopId, shops]);

  useEffect(() => {
    if (shops.length > 0 && !shopId && !shopsLoaded) {
      const storedShopId = localStorage.getItem('selectedShopId');
      if (storedShopId) {
        setShopId(storedShopId);
      }
      setShopsLoaded(true);
    }
  }, [shops, shopId, shopsLoaded]);

  const fetchShops = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/shops/with-details/`);
      if (!response.ok) throw new Error('Failed to fetch shops');
      const data = await response.json();
      setShops(data);
      return data;
    } catch (err) {
      setError('Failed to load shops');
      return [];
    }
  };

  // mode === 'silent': background refresh (no disabled buttons)
  const fetchCategories = async (mode) => {
    const silent = mode === 'silent';
    // Only the latest request is allowed to update the screen
    const requestId = ++categoriesRequestRef.current;

    if (!silent) setLoading(true);
    setError(null);

    try {
      let url = `${API_BASE_URL}/categories/`;

      const queryParams = new URLSearchParams();

      if (shopId && !isAllShopsMode && !isNoShopId) {
        queryParams.append('shop_id', shopId);
      }

      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        cache: 'no-store'
      });

      if (requestId !== categoriesRequestRef.current) return;

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {}
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (requestId !== categoriesRequestRef.current) return;

      const categoriesData = Array.isArray(data) ? data : (data.data || data.results || []);

      const transformedCards = categoriesData.map(category => ({
        id: category.catrgort_id,
        image: category.image_url || '/images/default-category.jpg',
        category: category.category_Name,
        selected: false,
        shopId: category.shop?.shop_id,
        shopName: category.shop?.shop_name
      }));

      transformedCards.sort((a, b) => {
        const idA = Number(a.id) || 0;
        const idB = Number(b.id) || 0;
        return idA - idB;
      });

      setCards(transformedCards);
      setSelectAll(false);

      if (shopId && !isAllShopsMode && !isNoShopId) {
        writeCategoriesCache(shopId, transformedCards);
      }

    } catch (err) {
      if (requestId === categoriesRequestRef.current) {
        setError('Failed to load categories: ' + err.message);
      }
    } finally {
      if (!silent) setLoading(false);
      if (requestId === categoriesRequestRef.current) setInitialLoading(false);
    }
  };

  const handleShopChangeInAddModal = (e) => {
    const newShopId = e.target.value;
    setNewCard(prev => ({ ...prev, selectedShopId: newShopId }));
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // All shops except the one currently being viewed
  const otherShops = shops.filter(s => String(s.shop_id) !== String(shopId));

  const resetMoveState = () => {
    setShowMoveList(false);
    setMoveShopIds([]);
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);
    setCards(prevCards =>
      prevCards.map(card => ({
        ...card,
        selected: (shopId && !isAllShopsMode && !isNoShopId)
          ? (card.shopId === parseInt(shopId) ? newSelectAll : card.selected)
          : newSelectAll
      }))
    );
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleSelectCard = (cardId) => {
    setCards(prevCards =>
      prevCards.map(card =>
        card.id === cardId ? { ...card, selected: !card.selected } : card
      )
    );
    setSelectAll(false);
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleToggleMoveShop = (id) => {
    const key = String(id);
    if (moveShopIds.includes(key)) {
      setMoveShopIds(moveShopIds.filter(x => x !== key));
    } else {
      setMoveShopIds([...moveShopIds, key]);
    }
  };

  // Full URL of a category image (so it can be downloaded and sent again)
  const getFullImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('/images/default-category')) return null;
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;
    return `https://super-backend.hira-tech.co.uk${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
  };

  const handleMoveSelected = async () => {
    const selectedCards = cards.filter(card => card.selected);
    if (selectedCards.length === 0) {
      Swal.fire({ icon: 'warning', title: 'No Selection', text: 'Please select categories first', confirmButtonColor: '#3085d6' });
      return;
    }
    if (moveShopIds.length === 0) {
      Swal.fire({ icon: 'warning', title: 'No Shop', text: 'Please select at least one shop', confirmButtonColor: '#3085d6' });
      return;
    }

    setMoveLoading(true);

    let successCount = 0;
    let failedCount = 0;
    let noImageCount = 0;

    try {
      // Download each image only once (it is reused for every target shop)
      const imageBlobs = {};
      for (const card of selectedCards) {
        const imgUrl = getFullImageUrl(card.image);
        if (!imgUrl) continue;
        try {
          const imgRes = await fetch(imgUrl);
          if (imgRes.ok) imageBlobs[card.id] = await imgRes.blob();
        } catch (imgErr) {}
      }

      for (const targetId of moveShopIds) {
        for (const card of selectedCards) {
          try {
            const formData = new FormData();
            formData.append('category_Name', card.category);
            formData.append('shop_id', parseInt(targetId));

            const blob = imageBlobs[card.id];
            if (blob) {
              formData.append('image', blob, 'category-image.jpg');
            } else {
              noImageCount++;
            }

            const response = await fetch(`${API_BASE_URL}/categories/create/`, {
              method: 'POST',
              body: formData,
            });
            if (response.ok) successCount++;
            else failedCount++;
          } catch (err) {
            failedCount++;
          }
        }
        // The target shop list must be loaded fresh (with the new images) next time
        clearCategoriesCache(targetId);
      }

      let message = '';
      if (successCount > 0 && failedCount === 0) {
        message = `${successCount} category(s) copied successfully!`;
      } else if (successCount > 0) {
        message = `${successCount} copied, ${failedCount} failed (maybe already exist).`;
      } else {
        message = 'Could not copy categories (maybe they already exist in those shops).';
      }
      if (noImageCount > 0 && successCount > 0) {
        message += ` Some images could not be copied.`;
      }

      Swal.fire({
        icon: successCount > 0 ? (failedCount > 0 || noImageCount > 0 ? 'warning' : 'success') : 'error',
        title: successCount > 0 ? 'Done' : 'Error',
        text: message,
        confirmButtonColor: '#3085d6'
      });

      setShowActionDropdown(false);
      resetMoveState();
      await fetchCategories('');
      setSelectAll(false);
    } finally {
      setMoveLoading(false);
    }
  };

  const handleDeleteSelected = async () => {
    const selectedCards = cards.filter(card => card.selected);
    if (selectedCards.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'No Selection',
        text: 'Please select at least one category to delete',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${selectedCards.length} category(s). This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      setLoading(true);
      let deletedCount = 0;

      for (const card of selectedCards) {
        try {
          const response = await fetch(`${API_BASE_URL}/categories/${card.id}/delete/`, {
            method: 'DELETE',
          });
          if (response.ok) deletedCount++;
        } catch (err) {}
      }

      await fetchCategories('');
      setSelectAll(false);

      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: `${deletedCount} category(s) deleted successfully!`,
        confirmButtonColor: '#3085d6'
      });
      setLoading(false);
    }
  };

  const handleEditClick = (card) => {
    setEditingCard(card);
    setEditFormData({
      id: card.id,
      image: card.image,
      imagePreview: card.image,
      category: card.category || '',
      shopId: card.shopId,
      shopName: card.shopName
    });
    setShowEditModal(true);
  };

  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  const handleEditSave = async () => {
    if (!editFormData.category.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please enter a category name',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('category_Name', editFormData.category);

      if (editFormData.shopId) {
        formData.append('shop_id', editFormData.shopId);
      } else if (shopId && !isAllShopsMode) {
        formData.append('shop_id', parseInt(shopId));
      }

      if (editFormData.image && editFormData.image.startsWith('data:image')) {
        const file = dataURLtoFile(editFormData.image, 'category-image.jpg');
        formData.append('image', file);
      }

      const url = `${API_BASE_URL}/categories/${editFormData.id}/update/`;

      const response = await fetch(url, {
        method: 'PUT',
        body: formData,
      });

      if (response.ok) {
        await fetchCategories('');
        setShowEditModal(false);
        setEditingCard(null);
        setEditFormData({
          id: null,
          image: null,
          imagePreview: null,
          category: '',
          shopId: null,
          shopName: ''
        });
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'Category updated successfully!',
          confirmButtonColor: '#3085d6'
        });
      } else {
        const errorText = await response.text();
        try {
          const errorData = JSON.parse(errorText);
          throw new Error(errorData.error || 'Failed to update category');
        } catch {
          throw new Error(`Server error: ${response.status}`);
        }
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update category: ' + err.message,
        confirmButtonColor: '#d33'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      Swal.fire({ icon: 'warning', title: 'Invalid File', text: 'Please select an image file', confirmButtonColor: '#3085d6' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({ icon: 'warning', title: 'File Too Large', text: 'File size should be less than 5MB', confirmButtonColor: '#3085d6' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setEditFormData(prev => ({
        ...prev,
        image: e.target.result,
        imagePreview: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleNewCardImage = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      Swal.fire({ icon: 'warning', title: 'Invalid File', text: 'Please select an image file', confirmButtonColor: '#3085d6' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      Swal.fire({ icon: 'warning', title: 'File Too Large', text: 'File size should be less than 5MB', confirmButtonColor: '#3085d6' });
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      setNewCard(prev => ({
        ...prev,
        image: e.target.result,
        imagePreview: e.target.result
      }));
    };
    reader.readAsDataURL(file);
  };

  // Label size: 40mm (width) x 10mm (height), text centered.
  // If the name is too long, the text shrinks automatically to fit the label.
  const handlePrintLabel = (card) => {
  const printWindow = window.open('', '_blank', 'width=800,height=600,left=100,top=100');
    if (!printWindow) {
      Swal.fire({
        icon: 'warning',
        title: 'Popup Blocked',
        text: 'Please allow popups to print labels',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    const safeName = escapeHtml(card.category);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Label - ${safeName}</title>
        <style>
          @page { size: 40mm 10mm; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          html, body {
            width: 40mm;
            height: 10mm;
            overflow: hidden;
            background: #ffffff;
            font-family: 'Arial', 'Helvetica', sans-serif;
          }
          .label {
            width: 40mm;
            height: 10mm;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            overflow: hidden;
            padding: 0 1mm;
          }
          .label-text {
            display: inline-block;
            font-size: 34px;
            font-weight: 700;
            color: #000000;
            line-height: 1;
            white-space: nowrap;
          }
          @media screen {
            body { margin: 20px auto; border: 1px dashed #999; }
          }
        </style>
      </head>
      <body>
        <div class="label" id="label"><span class="label-text" id="labelText">${safeName}</span></div>
        <script>
          window.onload = function () {
            var label = document.getElementById('label');
            var text = document.getElementById('labelText');
            var size = 34;
            var maxW = label.clientWidth - 8;
            var maxH = label.clientHeight - 2;
            while ((text.offsetWidth > maxW || text.offsetHeight > maxH) && size > 4) {
              size -= 1;
              text.style.fontSize = size + 'px';
            }
            window.print();
          };
        <\/script>
      </body>
      </html>
    `);

    printWindow.document.close();
  };

  const handleAddNewCard = async () => {
    if (!newCard.category.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Information',
        text: 'Please enter a category name',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    if (!newCard.image) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Image',
        text: 'Please select an image',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    if ((isAllShopsMode || isNoShopId) && !newCard.selectedShopId) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Shop',
        text: 'Please select a shop',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    const targetShopId = (isAllShopsMode || isNoShopId)
      ? parseInt(newCard.selectedShopId)
      : parseInt(shopId);

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('category_Name', newCard.category);
      formData.append('shop_id', targetShopId);

      if (newCard.image && newCard.image.startsWith('data:image')) {
        const file = dataURLtoFile(newCard.image, 'category-image.jpg');
        formData.append('image', file);
      }

      const response = await fetch(`${API_BASE_URL}/categories/create/`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        setShowAddModal(false);

        setNewCard({
          image: null,
          imagePreview: null,
          category: '',
          selectedShopId: ''
        });

        await new Promise(resolve => setTimeout(resolve, 300));
        await fetchCategories('');

        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'New category added successfully!',
          confirmButtonColor: '#3085d6'
        });
      } else {
        const errorText = await response.text();
        let errorMessage = 'Failed to create category';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || 'Failed to create category';
        } catch {
          errorMessage = `Server error: ${response.status}`;
        }

        if (errorMessage.includes('already exists')) {
          Swal.fire({
            icon: 'warning',
            title: 'Duplicate Entry',
            text: errorMessage,
            confirmButtonColor: '#3085d6'
          });
        } else {
          throw new Error(errorMessage);
        }
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to create category: ' + err.message,
        confirmButtonColor: '#d33'
      });
    } finally {
      setLoading(false);
    }
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setNewCard({
      image: null,
      imagePreview: null,
      category: '',
      selectedShopId: ''
    });
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingCard(null);
    setEditFormData({
      id: null,
      image: null,
      imagePreview: null,
      category: '',
      shopId: null,
      shopName: ''
    });
  };

  const handleOpenAddModal = () => {
    setShowAddModal(true);
  };

  const selectedCount = cards.filter(card => card.selected).length;

  const filteredCards = (shopId && !isAllShopsMode && !isNoShopId
    ? cards.filter(card => card.shopId === parseInt(shopId))
    : cards
  ).filter(card =>
    card.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClearAll = () => {
    setCards(prev => prev.map(card => ({ ...card, selected: false })));
    setSelectAll(false);
    setShowActionDropdown(false);
    resetMoveState();
  };

  const handleClickOutside = (e) => {
    if (showActionDropdown && !e.target.closest('.action-dropdown-container')) {
      setShowActionDropdown(false);
      resetMoveState();
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showActionDropdown]);

  const isBusy = loading || moveLoading;

  return (
    <div className="categories-main-container">

      {/* Heading + Search + Buttons */}
      <div className="categories-action-section">
        <div className="categories-heading">
          <h2>
            <FaTags className="categories-heading-icon" />
            Categories Management
          </h2>
        </div>

        <div className="categories-action-right">
          <div className="categories-search-wrapper">
            <FaSearch className="categories-search-icon" />
            <input
              type="text"
              placeholder="Search by Category Name..."
              value={searchTerm}
              onChange={handleSearch}
              className="categories-search-input"
              disabled={isBusy}
            />
          </div>

          <div className="categories-action-buttons">
            <button
              className="categories-select-all-btn"
              onClick={handleSelectAll}
              disabled={isBusy}
            >
              <FaCheckDouble />
              <span>
                {selectedCount === filteredCards.length && filteredCards.length > 0 ? 'Deselect All' : 'Select All'}
              </span>
            </button>
            <button
              className="categories-add-btn"
              onClick={handleOpenAddModal}
              disabled={isBusy}
            >
              <FaPlus />
              <span>ADD CATEGORY</span>
            </button>
          </div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {selectedCount > 0 && (
        <div className="selected-summary">
          <div className="summary-content">
            <span className="summary-icon">✓</span>
            <span className="summary-text">
              {selectedCount} category(s) selected
            </span>
            <div className="action-dropdown-container">
              <button
                className="action-dropdown-btn"
                onClick={() => {
                  setShowActionDropdown(!showActionDropdown);
                  resetMoveState();
                }}
              >
                <FaEllipsisV />
                <span>Actions</span>
              </button>
              {showActionDropdown && (
                <div className="action-dropdown-menu">
                  <button
                    className="dropdown-item"
                    onClick={() => setShowMoveList(!showMoveList)}
                    disabled={isBusy}
                  >
                    <FaChevronRight className={`move-arrow ${showMoveList ? 'move-arrow-open' : ''}`} />
                    <span>Move To</span>
                  </button>

                  {showMoveList && (
                    <div className="move-shops-panel">
                      <div className="move-shops-list">
                        {otherShops.length > 0 ? (
                          otherShops.map(shop => (
                            <label key={shop.shop_id} className="move-shop-item">
                              <input
                                type="checkbox"
                                checked={moveShopIds.includes(String(shop.shop_id))}
                                onChange={() => handleToggleMoveShop(shop.shop_id)}
                                disabled={moveLoading}
                              />
                              <span>{shop.shop_name}</span>
                            </label>
                          ))
                        ) : (
                          <div className="move-no-shops">No other shops</div>
                        )}
                      </div>
                      {otherShops.length > 0 && (
                        <button
                          className="move-apply-btn"
                          onClick={handleMoveSelected}
                          disabled={moveLoading || moveShopIds.length === 0}
                        >
                          {moveLoading ? 'Copying...' : `Copy to ${moveShopIds.length} shop(s)`}
                        </button>
                      )}
                    </div>
                  )}

                  <button
                    className="dropdown-item delete-item"
                    onClick={handleDeleteSelected}
                    disabled={isBusy}
                  >
                    <FaTrash />
                    <span>Delete Selected ({selectedCount})</span>
                  </button>
                </div>
              )}
            </div>
            <button className="clear-selection" onClick={handleClearAll} disabled={isBusy}>
              Clear all
            </button>
          </div>
        </div>
      )}

      {/* CARDS GRID - grey border wrapper */}
      <div className="categories-cards-wrapper">
        <div className="cards-grid">
          {filteredCards.map(card => (
            <div key={card.id} className="category-card">
              <div className="card-checkbox">
                <input
                  type="checkbox"
                  checked={card.selected || false}
                  onChange={() => handleSelectCard(card.id)}
                />
              </div>

              <div className="card-image-container">
                <img
                  src={card.image}
                  alt={card.category}
                  className="card-image"
                  onError={(e) => {
                    e.target.src = '/images/default-category.jpg';
                  }}
                />
              </div>

              <div className="card-content">
                <div className="view-mode">
                  <h3 className="card-title">{card.category}</h3>

                  {isAllShopsMode && (
                    <div className="card-shop-badge">
                      <FaStore style={{ marginRight: '5px', fontSize: '0.7rem' }} />
                      <span>{card.shopName} (ID: {card.shopId})</span>
                    </div>
                  )}

                  <div className="card-actions">
                    <button
                      className="edit-card-btn"
                      onClick={() => handleEditClick(card)}
                      disabled={isBusy}
                    >
                      <MdEditSquare /> Edit
                    </button>
                    <button
                      className="print-label-btn"
                      onClick={() => handlePrintLabel(card)}
                      disabled={isBusy}
                    >
                      <FaPrint /> Print Label
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredCards.length === 0 && !loading && !initialLoading && (
          <div className="categories-empty-state">
            <div className="empty-icon">📦</div>
            <h3>No Categories Found</h3>
            <p>Click "ADD CATEGORY" to create your first category</p>
            <button className="add-first-btn" onClick={handleOpenAddModal}>
              <FaPlus /> Add Your First Category
            </button>
          </div>
        )}
      </div>

      {/* Add New Category Modal - City removed */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="modal-content categories-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Category</h3>
              <button className="modal-close" onClick={closeAddModal}>×</button>
            </div>
            <div className="modal-body">
              {(isAllShopsMode || isNoShopId) ? (
                <div className="modal-form-group">
                  <label className="dropdown-label">SELECT SHOP:</label>
                  <select
                    className="modal-select"
                    value={newCard.selectedShopId}
                    onChange={handleShopChangeInAddModal}
                    required
                  >
                    <option value="">Select a shop</option>
                    {shops.map(shop => (
                      <option key={shop.shop_id} value={shop.shop_id}>
                        {shop.shop_name} (ID: {shop.shop_id})
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="modal-form-group">
                  <label className="dropdown-label">SHOP:</label>
                  <div className="shop-info-display">
                    <span className="shop-display-name">{shopName} (ID: {shopId})</span>
                  </div>
                </div>
              )}

              <div className="modal-form-group">
                <label>Category Name</label>
                <input
                  type="text"
                  value={newCard.category}
                  onChange={(e) => setNewCard({...newCard, category: e.target.value})}
                  placeholder="Enter category name (e.g., TiBase, Screw, Abutment, ScanBody)"
                  className="modal-input"
                />
                <small className="form-hint">This name will appear below the image on the card</small>
              </div>

              <div className="modal-image-section add-modal-image">
                <div className="image-preview-container add-image-preview">
                  {newCard.imagePreview ? (
                    <img src={newCard.imagePreview} alt="Preview" className="image-preview add-image-preview-img" />
                  ) : (
                    <div className="image-placeholder add-image-placeholder">
                      <span>📷</span>
                    </div>
                  )}
                  <label className="upload-image-btn add-upload-btn">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleNewCardImage}
                      style={{ display: 'none' }}
                    />
                    {newCard.imagePreview ? 'Change Image' : 'Upload Image'}
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel-btn" onClick={closeAddModal} disabled={loading}>
                Cancel
              </button>
              <button className="modal-save-btn" onClick={handleAddNewCard} disabled={loading}>
                {loading ? 'Adding...' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Category Modal - City removed */}
      {showEditModal && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content categories-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit Category</h3>
              <button className="modal-close" onClick={closeEditModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="modal-form-group">
                <label className="dropdown-label"><FaStore style={{ marginRight: '5px' }} />SHOP:</label>
                <div className="shop-info-display">
                  <FaStore className="shop-display-icon" />
                  <span className="shop-display-name">{editFormData.shopName} (ID: {editFormData.shopId})</span>
                </div>
              </div>

              <div className="modal-form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  value={editFormData.category}
                  onChange={(e) => setEditFormData({...editFormData, category: e.target.value})}
                  placeholder="Enter category name"
                  className="modal-input"
                />
                <small className="form-hint">This name will appear below the image on the card</small>
              </div>

              <div className="modal-image-section edit-modal-image">
                <div className="image-preview-container edit-image-preview">
                  {editFormData.imagePreview ? (
                    <img src={editFormData.imagePreview} alt="Preview" className="image-preview edit-image-preview-img" />
                  ) : (
                    <div className="image-placeholder edit-image-placeholder">
                      <span>📷</span>
                    </div>
                  )}
                  <label className="upload-image-btn edit-upload-btn">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      onChange={handleEditImageUpload}
                      style={{ display: 'none' }}
                    />
                    {editFormData.imagePreview ? 'Change Image' : 'Upload Image'}
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-cancel-btn" onClick={closeEditModal} disabled={loading}>
                Cancel
              </button>
              <button className="modal-save-btn" onClick={handleEditSave} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;